import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import { linkWalletQueryOptions, startLinkConnection } from "@/shared/linkWalletQueries.ts";

/** Shows Link setup and saved payment methods on the web wallet route. */
export const LinkWallet = () => {
  const flow = new URLSearchParams(window.location.search).get("flow") ?? undefined;
  const client = useQueryClient();
  const query = linkWalletQueryOptions(flow);
  const wallet = useQuery(query);
  const canConnect = !wallet.isError && !!wallet.data && wallet.data.connectionEnabled !== false;
  const [popupBlocked, setPopupBlocked] = useState(false);
  const connect = useMutation({
    mutationFn: (_signInWindow: Window | null) => startLinkConnection(flow),
    onSuccess: (data, signInWindow) => {
      client.setQueryData(query.queryKey, data);
      if (data.actionPath && signInWindow && !signInWindow.closed) {
        signInWindow.location.replace(resolveApiPath(data.actionPath));
      } else {
        signInWindow?.close();
      }
    },
    onError: async (_error, signInWindow) => {
      signInWindow?.close();
      // Setup may have reached Link even if its response was lost.
      await wallet.refetch();
    },
  });
  useEffect(() => {
    if (wallet.data?.status !== "connected") return;
    void client.invalidateQueries({ queryKey: ["connectorCatalog"] });
    void client.invalidateQueries({ queryKey: ["connectors"] });
    void client.invalidateQueries({ queryKey: ["shopping"] });
  }, [wallet.data?.status, client]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") {
        void wallet.refetch();
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [wallet.refetch]);

  const setupFailed =
    connect.isError && wallet.data?.status !== "connected" && !wallet.data?.actionPath;
  let connectLabel = "Reconnect Link";
  if (connect.isPending) connectLabel = "Opening Link…";
  else if (connect.isError) connectLabel = "Try again";
  else if (wallet.data?.status === "not_connected") connectLabel = "Connect Link";

  return (
    <main className="mx-auto flex max-w-xl flex-col gap-5 p-6">
      {/* The nav destination rather than the settings copy: "Back to
          Connectors" should land where the nav names Connectors. */}
      <Link
        href={wallet.data?.returnPath ?? "/connectors"}
        className="text-sm text-muted-foreground"
      >
        {wallet.data?.returnPath ? "Return to shopping" : "Back to Connectors"}
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Link</CardTitle>
          <CardDescription>
            Connect a saved card. You’ll approve each purchase before paying.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-4">
          <p className="text-sm text-muted-foreground">A US phone number is required.</p>
          {wallet.isPending && <output>Loading Link…</output>}
          {wallet.data?.connectionEnabled === false && <p>Payments are currently unavailable.</p>}
          {(wallet.isError || setupFailed) && (
            <p role="alert" className="text-sm text-destructive">
              {wallet.isError ? "Couldn’t load Link. Try again." : "Couldn’t open Link. Try again."}
            </p>
          )}
          {popupBlocked && wallet.data?.actionPath && (
            <p role="alert" className="text-sm text-destructive">
              Your browser blocked the sign-in tab. Open Link below to continue.
            </p>
          )}
          {wallet.data?.status === "connected" && (
            <div aria-live="polite" className="space-y-3">
              <p className="font-medium">Link is connected.</p>
              {wallet.data.methods.length > 0 ? (
                <ul className="space-y-2 text-sm">
                  {wallet.data.methods.map((method) => (
                    <li key={method.id}>{method.label}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No eligible saved cards are available yet. Add a card in Link before starting a
                  purchase.
                </p>
              )}
              <p className="text-sm text-muted-foreground">You can return to your conversation.</p>
            </div>
          )}
          {canConnect && wallet.data?.status === "connecting" && (
            <output className="text-sm">
              Finish signing in to Link. This page updates automatically.
            </output>
          )}
          {canConnect && wallet.data?.status === "needs_attention" && (
            <output className="text-sm">Sign in to Link again to reconnect your card.</output>
          )}
          {canConnect && !wallet.error && wallet.data?.actionPath ? (
            <Button asChild variant="ghost" data-statsig-name="link-wallet-continue">
              <a
                href={resolveApiPath(wallet.data.actionPath)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {popupBlocked ? "Open Link" : "Reopen Link"}
              </a>
            </Button>
          ) : (
            canConnect &&
            !wallet.error &&
            wallet.data &&
            wallet.data.status !== "connected" && (
              <Button
                data-statsig-name="link-wallet-connect"
                disabled={connect.isPending}
                onClick={() => {
                  // Open during the click so the browser permits the tab before the async setup.
                  const signInWindow = window.open("about:blank", "_blank");
                  if (signInWindow) signInWindow.opener = null;
                  setPopupBlocked(!signInWindow);
                  connect.mutate(signInWindow);
                }}
                variant="primary"
              >
                {connectLabel}
              </Button>
            )
          )}
          {wallet.isError && (
            <Button
              data-statsig-name="link-wallet-retry"
              variant="primary"
              disabled={wallet.isFetching}
              onClick={() => {
                void wallet.refetch();
              }}
            >
              Try again
            </Button>
          )}
        </CardContent>
      </Card>
    </main>
  );
};
